import { useState, useCallback, useRef, useEffect } from 'react';
import { randomUuid } from '../utils/randomUuid';
import useAiStream from './useAiStream';
import useAiSendSummary from './ai/useAiSendSummary';
import useAiCustomerListActions from './ai/useAiCustomerListActions';
import useQuickLogCompose from './ai/useQuickLogCompose';
import useAiAssistantVoice from './ai/useAiAssistantVoice';
import useAiAssistantActions from './ai/useAiAssistantActions';
import useAiAssistantErrors from './ai/useAiAssistantErrors';
import detectAiPageContext from '../utils/ai/detectAiPageContext';
import { isAiEnforcementCode } from '../utils/ai/normalizeAiError';
import {
  extractInlineChipsFromAssistantText,
  pushAssistantMessage,
  pushErrorAssistantMessage,
  buildAssistantHistoryForStream,
} from './ai/useAiChatMessageAppend';

export default function useAiAssistant({ userId, role, userName, onNavigate, pathname, tz, locale = 'en', voiceMessageLabel = 'Voice message' }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const messagesRef = useRef(messages);
  const clearQuickLogPreviewTimersRef = useRef(() => {});
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) { firstPathRef.current = false; return; }
    setPanelOpen(false);
  }, [pathname]);

  const {
    streamingText, streamingAction, streamingContext, streamingTranscript,
    isStreaming, streamPhase, startStream, abortStream, resetStream,
  } = useAiStream();

  const { confirmSendSummaryAction: confirmSendSummaryActionInner, sendSummaryInline, confirmBulkSendSummaryAction } = useAiSendSummary({
    role, messages, messagesRef, setMessages,
  });

  const detectContext = useCallback(
    () => detectAiPageContext(pathname),
    [pathname],
  );

  const { errorState, setError, clearError, setErrorFromException } = useAiAssistantErrors();

  const sendMessage = useCallback(async (text, options = {}) => {
    abortStream();
    resetStream();

    const silent = options.silent === true;
    const userMsg = { id: randomUuid(), role: 'user', text, createdAt: Date.now() };
    if (!silent) setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    const flipCtx = options.flipListContext || null;

    try {
      const baseCtx = { role, ...detectContext() };
      const context = options.composerCustomerId
        ? { ...baseCtx, composer_customer_id: String(options.composerCustomerId) }
        : baseCtx;
      // Bug 8: sync messagesRef synchronously before building history so rapid
      // back-to-back sends read current state, not the previous render's snapshot
      if (!silent) messagesRef.current = [...messagesRef.current, userMsg];
      const history = buildAssistantHistoryForStream(messagesRef, locale);

      const result = await startStream({
        input_type: 'text', text, context, displayName: userName || '', history, locale,
      });

      if (!result) return;

      const reply = (result.text || '').trim();
      const action = result.action || null;
      // Allow empty text when an action card will render — the card IS the message.
      // Only treat a truly empty reply (no text, no action) as a failure.
      if (!reply && !action) throw new Error('Empty response from assistant');
      const flip =
        action?.type === 'open_log_job' && flipCtx?.listMessageId && flipCtx?.customerId
          ? { listMessageId: flipCtx.listMessageId, customerId: flipCtx.customerId, kind: 'log_done' }
          : null;
      const inline_chips = extractInlineChipsFromAssistantText(result.text);
      pushAssistantMessage(setMessages, { text: reply, action, streamContext: result.context || null, inline_chips, flip });
    } catch (err) {
      setErrorFromException(err, 'network');
      // Licensing / cap / not-enabled blocks already surface via the sticky
      // AiErrorBanner. Skip the inline chat-bubble so the user doesn't see
      // a stacked "Request failed" string on top of the banner.
      if (!isAiEnforcementCode(err?.code)) {
        pushErrorAssistantMessage(setMessages, err, locale);
      }
    } finally {
      setLoading(false);
    }
  }, [role, userName, locale, detectContext, startStream, abortStream, resetStream, setErrorFromException]);

  const {
    handleRowAction,
    onChildActionCompleted,
    onChildActionDismissed,
    onNextStopLog,
    setListRowLock,
  } = useAiCustomerListActions({
    role,
    messagesRef,
    setMessages,
    sendMessage,
    beforeQuickLogCompose: () => clearQuickLogPreviewTimersRef.current(),
  });

  const {
    onQuickLogSubmit,
    onQuickLogCancel,
    onQuickLogUndo, // eslint-disable-line no-unused-vars
    onQuickLogVoiceToggle,
    quickLogRecordingId,
    quickLogRecordingSeconds,
  } = useQuickLogCompose({
    role, locale, userName, detectContext, startStream,
    setMessages, setLoading, sendMessage, messagesRef,
    clearQuickLogPreviewTimersRef, setListRowLock,
  });

  const { uploadVoiceBlob, abortVoiceUpload } = useAiAssistantVoice({
    role, userName, locale, voiceMessageLabel, detectContext, setMessages, setLoading, startStream,
    abortStream, setErrorFromException, clearError,
  });

  const {
    executeAction, approveAction, dismissAction, confirmFollowUpAction,
    confirmSendSummaryAction, confirmLifecycleAction, confirmEodCloseoutAction,
    requestPostJobSummaryFromCta,
  } = useAiAssistantActions({
    messages, messagesRef, setMessages, setPanelOpen, onNavigate, role, tz, locale,
    onChildActionCompleted,
    onChildActionDismissed,
    confirmSendSummaryActionInner, confirmBulkSendSummaryAction, sendSummaryInline,
  });

  const stopResponse = useCallback(() => { abortStream(); setLoading(false); }, [abortStream]);

  const resetChat = useCallback(() => {
    abortStream();
    // Bug 4: also abort any in-flight voice upload so transcript doesn't appear in empty chat
    abortVoiceUpload();
    resetStream();
    try { clearQuickLogPreviewTimersRef.current?.(); } catch {}
    setMessages([]);
    setLoading(false);
  }, [abortStream, abortVoiceUpload, resetStream]);

  // User isolation: wipe chat + any in-flight work whenever the signed-in user changes.
  // Why: the hook is mounted at app root and never unmounts across logout/login, so without
  // this effect supervisor/worker/admin chats would leak across user sessions on the same device.
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    if (lastUserIdRef.current === userId) return;
    lastUserIdRef.current = userId;
    resetChat();
    setPanelOpen(false);
    clearError();
  }, [userId, resetChat, clearError]);

  const togglePanel = useCallback(() => setPanelOpen((p) => !p), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const onInlineChipTap = useCallback((messageId, chipId, prompt) => {
    if (messageId) {
      setMessages((prev) => prev.map((m) =>
        m.id === messageId && !m._chipsLockedBy ? { ...m, _chipsLockedBy: chipId || true } : m,
      ));
    }
    if (prompt) sendMessage(prompt);
  }, [sendMessage]);

  return {
    messages, loading, panelOpen, sendMessage, stopResponse, resetChat,
    executeAction, approveAction, dismissAction, confirmFollowUpAction,
    confirmLifecycleAction, confirmSendSummaryAction, confirmEodCloseoutAction, requestPostJobSummaryFromCta,
    onRowAction: handleRowAction, onNextStopLog, onQuickLogSubmit, onQuickLogCancel, onQuickLogUndo, onQuickLogVoiceToggle,
    quickLogRecordingId, quickLogRecordingSeconds, togglePanel, closePanel,
    uploadVoiceBlob,
    errorState, setError, clearError,
    streamingText, streamingAction, streamingContext, streamingTranscript, isStreaming, streamPhase,
    onInlineChipTap,
  };
}
